// React imports
import { useState, useContext, useMemo, useEffect } from "react";
// Context
import { AlertNotificationContext } from "../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../hooks/useCheckAPI";
// State Import
import { useItemPriceState } from "../state/itemPriceState";

// Simple in-memory cache for prices keyed by SKU (itemNo)
// Stores { value: number, ts: number }
const priceCache = new Map();
const PRICE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const clearPriceCache = () => priceCache.clear();

export function useItemPrice(itemNo, enabled) {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  // Determine if we have a fresh cached value
  const cacheEntry = useMemo(() => {
    if (!itemNo) return null;
    return priceCache.get(itemNo) || null;
  }, [itemNo]);

  const isFresh = cacheEntry
    ? Date.now() - cacheEntry.ts < PRICE_TTL_MS
    : false;

  // Stale‑while‑revalidate: keep stale value visible while refetching
  const [price, setPrice] = useState(() => cacheEntry?.value ?? 0);

  // Sync local state if cache gets populated for new SKU
  useEffect(() => {
    if (!itemNo) return;
    if (cacheEntry && cacheEntry.value !== price) {
      setPrice(cacheEntry.value); // sync to cache
    }
    // IMPORTANT: Do NOT clear to 0 when cacheEntry missing; keep last price for UX
    // New SKU will naturally have price 0 if first time (initial state), but we avoid flicker on re-render.
  }, [itemNo, cacheEntry]);

  // Fetch if no cache OR cache stale
  const shouldFetch = enabled && !!itemNo && (!cacheEntry || !isFresh);

  const { data, isSuccess } = useItemPriceState(itemNo, shouldFetch);
  // Success Function
  const successFunc = () => {
    // Safely extract price from possible response shapes
    const priceValue =
      data?.data?.data?.data?.data?.price ??
      data?.data?.data?.data?.price ??
      data?.data?.data?.price ??
      data?.data?.price ??
      data?.price;
    const priceNumber = priceValue !== undefined ? Number(priceValue) : 0;
    if (!isNaN(priceNumber)) {
      setPrice((prev) => {
        // Avoid state update if unchanged
        if (prev === priceNumber) return prev;
        return priceNumber;
      });
      priceCache.set(itemNo, { value: priceNumber, ts: Date.now() });
    } else {
      setPrice(0);
    }
  };

  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data?.data?.status ?? "Unknown"}: ${
        data?.data?.statusText ?? "Unknown error"
      }`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  return price;
}
